import React, { useState, useMemo } from 'react';
import {
  Search, X, Eye, ChevronRight, FileText, User, Clock,
  Camera, Video, FileCheck, Shield,
  UserCheck, ShoppingCart, Truck, Receipt, AlertCircle,
  Users, QrCode, Link, CheckCircle
} from 'lucide-react';
import {
  OPERATION_TYPES,
  OPERATION_TYPE_LABELS,
  OPERATION_GROUPS,
  EVIDENCE_TYPES,
  EVIDENCE_TYPE_LABELS,
  DOCUMENT_TYPE_LABELS,
} from '../constants/operationLog';

// 操作类型图标映射
const OPERATION_ICONS = {
  [OPERATION_TYPES.USER_REGISTER]: User,
  [OPERATION_TYPES.ID_CARD_UPLOAD]: FileText,
  [OPERATION_TYPES.BANK_CARD_UPLOAD]: FileText,
  [OPERATION_TYPES.AUTH_APPROVED]: UserCheck,
  [OPERATION_TYPES.ORDER_CREATE]: ShoppingCart,
  [OPERATION_TYPES.ORDER_AGREE_CONTRACT]: FileCheck,
  [OPERATION_TYPES.ORDER_SUBMIT]: ShoppingCart,
  [OPERATION_TYPES.ORDER_FACE_VERIFY]: Camera,
  // 帮别人卖 - 组织者
  [OPERATION_TYPES.ORGANIZER_CREATE_ORDER]: Users,
  [OPERATION_TYPES.ORGANIZER_FILL_SUPPLIER]: User,
  [OPERATION_TYPES.ORGANIZER_GENERATE_QR]: QrCode,
  [OPERATION_TYPES.ORGANIZER_SHARE_LINK]: Link,
  // 帮别人卖 - 实供人
  [OPERATION_TYPES.SUPPLIER_SCAN_QR]: QrCode,
  [OPERATION_TYPES.SUPPLIER_VERIFY_PHONE]: UserCheck,
  [OPERATION_TYPES.SUPPLIER_CONFIRM_ORDER]: CheckCircle,
  [OPERATION_TYPES.SUPPLIER_SELECT_BANK]: FileText,
  [OPERATION_TYPES.SUPPLIER_AGREE_CONTRACT]: FileCheck,
  [OPERATION_TYPES.SUPPLIER_FACE_VERIFY]: Camera,
  // 其他
  [OPERATION_TYPES.RESERVATION_CREATE]: Truck,
  [OPERATION_TYPES.RESERVATION_SUBMIT]: Truck,
  [OPERATION_TYPES.SETTLEMENT_CONFIRM]: Receipt,
  [OPERATION_TYPES.SETTLEMENT_AGREE_CONTRACT]: FileCheck,
  [OPERATION_TYPES.SETTLEMENT_FACE_VERIFY]: Camera,
  [OPERATION_TYPES.DOCUMENT_VIEW]: Eye,
};

// 存证类型图标映射
const EVIDENCE_ICONS = {
  [EVIDENCE_TYPES.NONE]: null,
  [EVIDENCE_TYPES.IMAGE]: Camera,
  [EVIDENCE_TYPES.VIDEO]: Video,
  [EVIDENCE_TYPES.FACE_IMAGE]: Camera,
  [EVIDENCE_TYPES.FACE_VIDEO]: Video,
  [EVIDENCE_TYPES.ID_CARD_FRONT]: FileText,
  [EVIDENCE_TYPES.ID_CARD_BACK]: FileText,
  [EVIDENCE_TYPES.BANK_CARD]: FileText,
  [EVIDENCE_TYPES.CONTRACT]: FileCheck,
  [EVIDENCE_TYPES.QR_CODE]: QrCode,
  [EVIDENCE_TYPES.SCREENSHOT]: Camera,
};

// 存证类型颜色映射
const EVIDENCE_COLORS = {
  [EVIDENCE_TYPES.NONE]: 'text-slate-400',
  [EVIDENCE_TYPES.IMAGE]: 'text-blue-500',
  [EVIDENCE_TYPES.VIDEO]: 'text-purple-500',
  [EVIDENCE_TYPES.FACE_IMAGE]: 'text-green-500',
  [EVIDENCE_TYPES.FACE_VIDEO]: 'text-green-600',
  [EVIDENCE_TYPES.ID_CARD_FRONT]: 'text-orange-500',
  [EVIDENCE_TYPES.ID_CARD_BACK]: 'text-orange-500',
  [EVIDENCE_TYPES.BANK_CARD]: 'text-cyan-500',
  [EVIDENCE_TYPES.CONTRACT]: 'text-indigo-500',
  [EVIDENCE_TYPES.QR_CODE]: 'text-pink-500',
  [EVIDENCE_TYPES.SCREENSHOT]: 'text-teal-500',
};

// 操作分组颜色
const GROUP_COLORS = {
  registration: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  order: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  organizer: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  supplier: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  reservation: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  settlement: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  document: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
};

// 统计卡片组件
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor, onClick, active }) => (
  <button
    onClick={onClick}
    className={`bg-white rounded-xl p-5 shadow-sm border transition-all w-full text-left ${
      active ? 'ring-2 ring-blue-500 border-blue-300' : 'border-slate-100 hover:border-slate-200'
    }`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </button>
);

// 操作日志行组件
const LogRow = ({ log, onViewDetail }) => {
  const OperationIcon = OPERATION_ICONS[log.operationType] || FileText;
  const EvidenceIcon = EVIDENCE_ICONS[log.evidenceType];
  const groupColor = GROUP_COLORS[log.operationGroup] || GROUP_COLORS.document;

  // 格式化时间
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}-${day} ${hours}:${minutes}`;
  };

  return (
    <tr
      className="hover:bg-slate-50 transition-colors cursor-pointer"
      onClick={() => onViewDetail(log)}
    >
      <td className="p-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 ${groupColor.bg} rounded-lg flex items-center justify-center`}>
            <OperationIcon size={16} className={groupColor.text} />
          </div>
          <span className="text-sm text-slate-800 font-medium">
            {OPERATION_TYPE_LABELS[log.operationType] || log.operationType}
          </span>
        </div>
      </td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
            {log.userName.charAt(0)}
          </div>
          <div>
            <p className="text-sm text-slate-800">{log.userName}</p>
            <p className="text-xs text-slate-400">{log.userPhone}</p>
          </div>
        </div>
      </td>
      <td className="p-3">
        {log.documentNo ? (
          <div>
            <p className="text-sm font-mono text-slate-700">{log.documentNo}</p>
            <p className="text-xs text-slate-400">
              {DOCUMENT_TYPE_LABELS[log.documentType] || log.documentType}
            </p>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">-</span>
        )}
      </td>
      <td className="p-3">
        {EvidenceIcon ? (
          <div className="flex items-center gap-1.5">
            <EvidenceIcon size={14} className={EVIDENCE_COLORS[log.evidenceType]} />
            <span className={`text-xs ${EVIDENCE_COLORS[log.evidenceType]}`}>
              {EVIDENCE_TYPE_LABELS[log.evidenceType]}
            </span>
          </div>
        ) : (
          <span className="text-slate-400 text-xs">无存证</span>
        )}
      </td>
      <td className="p-3">
        <span className="text-xs text-slate-600">{formatTime(log.timestamp)}</span>
      </td>
      <td className="p-3">
        <p className="text-sm text-slate-600 max-w-xs truncate" title={log.description}>
          {log.description}
        </p>
      </td>
      <td className="p-3">
        <button
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetail(log);
          }}
        >
          <Eye size={14} />
          详情
          <ChevronRight size={14} />
        </button>
      </td>
    </tr>
  );
};

// 主组件
export default function OperationLogView({ logs }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterEvidence, setFilterEvidence] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  // 计算统计数据
  const stats = useMemo(() => {
    const registration = logs.filter(l => l.operationGroup === 'registration').length;
    const order = logs.filter(l => l.operationGroup === 'order').length;
    const organizer = logs.filter(l => l.operationGroup === 'organizer').length;
    const supplier = logs.filter(l => l.operationGroup === 'supplier').length;
    const reservation = logs.filter(l => l.operationGroup === 'reservation').length;
    const settlement = logs.filter(l => l.operationGroup === 'settlement').length;
    const document = logs.filter(l => l.operationGroup === 'document').length;
    const withEvidence = logs.filter(l => l.evidenceType && l.evidenceType !== EVIDENCE_TYPES.NONE).length;
    const organizerMode = logs.filter(l => l.isOrganizerMode).length;

    return {
      total: logs.length,
      registration,
      order,
      organizer,
      supplier,
      reservation,
      settlement,
      document,
      withEvidence,
      organizerMode,
    };
  }, [logs]);

  // 提取用户列表
  const users = useMemo(() => {
    const userMap = new Map();
    logs.forEach(l => {
      if (!userMap.has(l.userId)) {
        userMap.set(l.userId, { id: l.userId, name: l.userName, phone: l.userPhone });
      }
    });
    return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [logs]);

  // 是否有活跃筛选
  const hasActiveFilters = searchQuery || filterGroup || filterUser || filterEvidence || filterDateStart || filterDateEnd;

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterGroup('');
    setFilterUser('');
    setFilterEvidence('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // 筛选日志
  const filteredLogs = useMemo(() => {
    let result = [...logs];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(l =>
        l.userName.toLowerCase().includes(query) ||
        l.userPhone.includes(query) ||
        l.description.toLowerCase().includes(query) ||
        (l.documentNo && l.documentNo.toLowerCase().includes(query))
      );
    }

    // 分组筛选
    if (filterGroup) {
      result = result.filter(l => l.operationGroup === filterGroup);
    }

    // 用户筛选
    if (filterUser) {
      result = result.filter(l => l.userId === filterUser);
    }

    // 存证筛选
    if (filterEvidence === 'with_evidence') {
      result = result.filter(l => l.evidenceType && l.evidenceType !== EVIDENCE_TYPES.NONE);
    } else if (filterEvidence === 'without_evidence') {
      result = result.filter(l => !l.evidenceType || l.evidenceType === EVIDENCE_TYPES.NONE);
    }

    // 日期范围筛选
    if (filterDateStart) {
      result = result.filter(l => l.timestamp && l.timestamp >= filterDateStart);
    }
    if (filterDateEnd) {
      result = result.filter(l => l.timestamp && l.timestamp <= filterDateEnd + 'T23:59:59');
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return result;
  }, [logs, searchQuery, filterGroup, filterUser, filterEvidence, filterDateStart, filterDateEnd]);

  // 分组点击处理
  const handleGroupClick = (group) => {
    setFilterGroup(filterGroup === group ? '' : group);
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">税务查证</h1>
          <p className="text-slate-500 text-sm mt-1">记录用户交易过程中的关键操作和存证信息，满足税务审计要求</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
          <Shield size={16} className="text-blue-500" />
          <span className="text-sm text-blue-700">税务查证</span>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
        <StatCard
          title="全部存证"
          value={stats.total}
          icon={FileText}
          iconColor="text-slate-600"
          bgColor="bg-slate-50"
          onClick={() => setFilterGroup('')}
          active={!filterGroup}
        />
        <StatCard
          title="用户注册"
          value={stats.registration}
          icon={User}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
          onClick={() => handleGroupClick('registration')}
          active={filterGroup === 'registration'}
        />
        <StatCard
          title="发起订单"
          value={stats.order}
          icon={ShoppingCart}
          iconColor="text-green-600"
          bgColor="bg-green-50"
          onClick={() => handleGroupClick('order')}
          active={filterGroup === 'order'}
        />
        <StatCard
          title="组织者代客"
          value={stats.organizer}
          icon={Users}
          iconColor="text-cyan-600"
          bgColor="bg-cyan-50"
          onClick={() => handleGroupClick('organizer')}
          active={filterGroup === 'organizer'}
        />
        <StatCard
          title="实供人确认"
          value={stats.supplier}
          icon={UserCheck}
          iconColor="text-rose-600"
          bgColor="bg-rose-50"
          onClick={() => handleGroupClick('supplier')}
          active={filterGroup === 'supplier'}
        />
        <StatCard
          title="预约发货"
          value={stats.reservation}
          icon={Truck}
          iconColor="text-amber-600"
          bgColor="bg-amber-50"
          onClick={() => handleGroupClick('reservation')}
          active={filterGroup === 'reservation'}
        />
        <StatCard
          title="确认结算"
          value={stats.settlement}
          icon={Receipt}
          iconColor="text-purple-600"
          bgColor="bg-purple-50"
          onClick={() => handleGroupClick('settlement')}
          active={filterGroup === 'settlement'}
        />
        <StatCard
          title="有存证"
          value={stats.withEvidence}
          icon={Camera}
          iconColor="text-red-600"
          bgColor="bg-red-50"
          onClick={() => setFilterEvidence(filterEvidence === 'with_evidence' ? '' : 'with_evidence')}
          active={filterEvidence === 'with_evidence'}
        />
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索用户姓名、手机号、单据号..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 用户筛选 */}
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部用户</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name} ({user.phone})</option>
              ))}
            </select>

            {/* 存证筛选 */}
            <select
              value={filterEvidence}
              onChange={(e) => setFilterEvidence(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部存证</option>
              <option value="with_evidence">有存证</option>
              <option value="without_evidence">无存证</option>
            </select>

            {/* 日期范围 */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <span className="text-slate-400">~</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 表格区域 */}
        {filteredLogs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">暂无操作日志</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作类型</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作用户</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">关联单据</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">存证信息</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作时间</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作描述</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.map(log => (
                  <LogRow
                    key={log.id}
                    log={log}
                    onViewDetail={setSelectedLog}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部统计 */}
        {filteredLogs.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredLogs.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{logs.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {selectedLog && (
        <OperationLogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}

// 详情弹窗组件
function OperationLogDetailModal({ log, onClose }) {
  const OperationIcon = OPERATION_ICONS[log.operationType] || FileText;
  const EvidenceIcon = EVIDENCE_ICONS[log.evidenceType];
  const groupColor = GROUP_COLORS[log.operationGroup] || GROUP_COLORS.document;

  // 格式化完整时间
  const formatFullTime = (timestamp) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* 头部 */}
        <div className={`${groupColor.bg} ${groupColor.border} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${groupColor.bg} rounded-xl flex items-center justify-center border ${groupColor.border}`}>
                <OperationIcon size={20} className={groupColor.text} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  {OPERATION_TYPE_LABELS[log.operationType]}
                </h2>
                <p className="text-sm text-slate-500">{OPERATION_GROUPS[log.operationGroup]?.label}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <X size={20} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* 用户信息 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              操作用户
            </h3>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-lg font-bold text-white">
                {log.userName.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-800">{log.userName}</p>
                <p className="text-sm text-slate-500">{log.userPhone}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">用户ID</p>
                <p className="text-sm font-mono text-slate-600">{log.userId}</p>
              </div>
            </div>
          </div>

          {/* 时间和地点 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              时间与地点
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Clock size={16} />
                  <span className="text-xs">操作时间</span>
                </div>
                <p className="font-mono text-slate-800">{formatFullTime(log.timestamp)}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <AlertCircle size={16} />
                  <span className="text-xs">IP地址</span>
                </div>
                <p className="font-mono text-slate-800">{log.deviceInfo?.ip || '-'}</p>
              </div>
            </div>
          </div>

          {/* 设备信息 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              设备信息
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">设备</p>
                <p className="text-sm text-slate-700">{log.deviceInfo?.device || '-'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">操作系统</p>
                <p className="text-sm text-slate-700">{log.deviceInfo?.os || '-'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">浏览器</p>
                <p className="text-sm text-slate-700">{log.deviceInfo?.browser || '-'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">地理位置</p>
                <p className="text-sm text-slate-700">{log.deviceInfo?.location || '-'}</p>
              </div>
            </div>
          </div>

          {/* 关联单据 */}
          {log.documentNo && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                关联单据
              </h3>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-500 mb-1">
                      {DOCUMENT_TYPE_LABELS[log.documentType]}
                    </p>
                    <p className="font-mono text-blue-800 font-medium">{log.documentNo}</p>
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {log.documentId}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 帮别人卖模式信息 */}
          {log.isOrganizerMode && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                代客下单信息
              </h3>
              <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-xl space-y-3">
                <div className="flex items-center gap-2 text-cyan-600 mb-3">
                  <Users size={18} />
                  <span className="font-medium">帮别人卖模式</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-cyan-500 mb-1">组织者</p>
                    <p className="text-sm text-slate-800 font-medium">{log.organizerName}</p>
                    <p className="text-xs text-slate-400">{log.organizerId}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-cyan-500 mb-1">实供人</p>
                    <p className="text-sm text-slate-800 font-medium">{log.supplierName}</p>
                    <p className="text-xs text-slate-400">{log.supplierPhone}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 存证信息 */}
          {log.evidenceType && log.evidenceType !== EVIDENCE_TYPES.NONE && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                存证信息
              </h3>
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  {EvidenceIcon && <EvidenceIcon size={18} className="text-green-600" />}
                  <span className="font-medium text-green-800">
                    {EVIDENCE_TYPE_LABELS[log.evidenceType]}
                  </span>
                </div>
                {log.evidenceUrl && (
                  <div className="space-y-2">
                    {log.evidenceThumbnail && (
                      <div className="bg-white rounded-lg p-2 inline-block">
                        <img
                          src={log.evidenceThumbnail}
                          alt="存证缩略图"
                          className="w-32 h-32 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    <p className="text-xs text-green-600 font-mono break-all">
                      {log.evidenceUrl}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 业务数据快照 */}
          {log.snapshot && Object.keys(log.snapshot).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                操作数据快照
              </h3>
              <div className="bg-slate-50 rounded-xl p-4">
                <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
                  {JSON.stringify(log.snapshot, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* 备注 */}
          {log.remark && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                备注信息
              </h3>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">{log.remark}</p>
              </div>
            </div>
          )}
        </div>

        {/* 底部 */}
        <div className="px-6 py-4 bg-slate-50 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
